import { getFeedbackToneVars } from './tones';

/**
 * stylesItems.js — 미팅지도 전화·콘텐츠·목록·카드·결과칩 스타일
 *
 * 포함 스타일:
 *   - 전화번호 인라인 링크
 *   - 콘텐츠 필드 행
 *   - 목록 테이블 (table-layout:fixed)
 *   - 모바일 카드
 *   - 결과 chip (계약됨 / 취소 / 피드백 / 잠금)
 *   - 결과 입력 버튼
 */

// ── 전화번호 ────────────────────────────────────────────────────────────────────

/** 전화번호 인라인 래퍼: flex wrap */
export const phoneInlineStyle = {
  display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 4,
};

/** '/' 구분자 */
export const phoneSlashStyle  = { fontSize: 13, color: 'var(--text-muted)', fontWeight: 700 };
/** '(' ')' 괄호 */
export const phoneParenStyle  = { fontSize: 13, color: 'var(--text-muted)', fontWeight: 700 };
/** 전화번호 링크 래퍼 */
export const phoneLinksWrapStyle = { display: 'inline-flex', flexWrap: 'wrap', gap: 8 };

/** 전화번호 링크 (tel:) */
export const phoneLinkStyle = {
  fontSize: 13, color: 'var(--mm-tone-cyan-text)', textDecoration: 'underline',
  textUnderlineOffset: 2, fontWeight: 700,
};

/** 전화번호 없음 표시 */
export const phoneEmptyStyle = { fontSize: 13, color: 'var(--border)' };

// ── 콘텐츠 영역 ────────────────────────────────────────────────────────────────

/** 콘텐츠 항목 행: 라벨 + 값 grid */
export const contentRowStyle  = { display: 'grid', gap: 3 };

/** 콘텐츠 라벨: small caps 회색 */
export const contentLabelStyle = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
};

/**
 * 콘텐츠 값 영역: 최대 높이 제한 + 스크롤
 * 내용이 길어도 시트 전체 높이가 지도를 덮지 않도록 제한
 */
export const contentValueStyle = {
  fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.5, wordBreak: 'break-word',
  maxHeight: 80, overflowY: 'auto', overscrollBehavior: 'contain',
};

/** 빈 상태 (데이터 없음) */
export const emptyStyle = {
  textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: 13,
};

/** 목록 래퍼: 최대 높이 + 스크롤 */
export const listWrapStyle = {
  display: 'grid', gap: 4, maxHeight: '68vh', overflowY: 'auto',
  paddingRight: 2, overscrollBehavior: 'contain',
};

// ── 목록 테이블 (table-layout:fixed 방식) ─────────────────────────────────────

/** 가로 스크롤 래퍼 */
export const listTableWrapStyle = {
  overflowX: 'auto',
  border: '1px solid var(--border)',
  borderRadius: 8,
  minWidth: 0,
};

/**
 * 일반 td 셀: 1줄 말줄임
 * maxWidth:0 → table-layout:fixed 환경에서 overflow:hidden 강제 적용 필수 트릭
 */
export const listTdStyle = {
  padding: '6px 8px',
  fontSize: 11,
  color: 'var(--text-sub)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 0,
  borderRight: '1px solid var(--bg-subtle)',
  verticalAlign: 'middle',
};

/** 상품+결과 td: 계약 시 칩이 추가되어 행 높이 자동 확장 */
export const listTdResultStyle = {
  padding: '4px 8px',
  fontSize: 11,
  color: 'var(--text-sub)',
  borderRight: '1px solid var(--bg-subtle)',
  verticalAlign: 'middle',
};

// ── 모바일 카드 ────────────────────────────────────────────────────────────────

/** 모바일 목록 카드 */
export const cardStyle = {
  border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-card)', padding: '12px 14px',
  display: 'grid', gap: 6,
};

/** 카드 상단: 회사명 + 우측 액션 버튼 */
export const cardTopStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
};

/** 경고 텍스트 (미팅 임박 등) */
export const warnTextStyle = { color: 'var(--mm-tone-amber-text)', fontSize: 12, fontWeight: 700 };

// ── 결과 chip ─────────────────────────────────────────────────────────────────

/** 결과 chip 행 래퍼 */
export const resultChipRowStyle = { display: 'flex', flexWrap: 'wrap', gap: 5 };

/** 계약됨 chip (초록) */
export const contractedChipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 3,
  background: 'var(--mm-tone-green-bg)', color: 'var(--mm-tone-green-text)', border: '1px solid var(--mm-tone-green-border)',
  borderRadius: 999, padding: '3px 9px', fontSize: 12, fontWeight: 700,
};

/** 계약취소 chip (주황) */
export const cancelledChipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 3,
  background: 'var(--mm-tone-red-bg)', color: 'var(--mm-tone-red-text)', border: '1px solid var(--mm-tone-red-border)',
  borderRadius: 999, padding: '3px 9px', fontSize: 12, fontWeight: 700,
};

/** 피드백 chip (보라) */
export const feedbackChipStyle = (colorKey = 'purple') => {
  const tone = getFeedbackToneVars(colorKey, 'purple');
  return {
  display: 'inline-block',
  background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
  borderRadius: 999, padding: '3px 9px', fontSize: 12, fontWeight: 700,
  };
};

/** 계약 접수 완료 잠금 표시 (바텀시트) */
export const resultLockedStyle = {
  width: '100%', textAlign: 'center',
  fontSize: 12, fontWeight: 700, color: 'var(--mm-tone-green-text)',
  background: 'var(--mm-tone-green-bg)', border: '1px solid var(--mm-tone-green-border)',
  borderRadius: 10, padding: '10px 0', marginTop: 2,
};

/** 목록 카드 계약완료 잠금 아이콘 */
export const cardLockedIconStyle = {
  fontSize: 14, lineHeight: 1,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, flexShrink: 0,
};

/** 바텀시트 결과 입력 버튼 (가로 full) */
export const resultEntryBtnStyle = {
  width: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  border: '1px solid var(--border)', borderRadius: 10,
  background: 'var(--bg-subtle)', color: 'var(--text-sub)',
  padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  marginTop: 2,
};

/**
 * 목록 카드 연필 아이콘 버튼: 결과 있으면 indigo, 없으면 neutral
 * @param {boolean} hasResult 결과 입력 여부
 */
export const cardResultIconBtnStyle = (hasResult, colorKey = 'purple') => {
  const tone = getFeedbackToneVars(colorKey, 'purple');
  return {
  width: 28, height: 28,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: `1px solid ${hasResult ? tone.border : 'var(--border)'}`,
  borderRadius: 8,
  background: hasResult ? tone.bg : 'var(--bg-subtle)',
  color: hasResult ? tone.text : 'var(--text-muted)',
  cursor: 'pointer', padding: 0, flexShrink: 0,
  };
};
